import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  Skeleton,
  Spacer,
  Tag,
  TagLabel,
  Accordion,
  Flex,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
  date as commonDate,
} from '@finley-technologies/common';

import {
  WorkflowUserApprovalGraphNodeWithStateGQLType,
  useGetWorkflowInstanceWithStateQuery,
  WorkflowStringArrayComparatorGQLType,
  UserApprovalForDomainGQLType,
  WorkflowGraphEdgesGQLType,
  WorkflowGraphObjectWithStateGQLType,
  useGetOrganizationQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const _constructWorkflowGraphNodesListHelper = ({
  currentNode,
  edgesByFrom,
  nodesById,
}: {
  currentNode: WorkflowUserApprovalGraphNodeWithStateGQLType;
  edgesByFrom: { [key: string]: WorkflowGraphEdgesGQLType };
  nodesById: { [key: string]: WorkflowUserApprovalGraphNodeWithStateGQLType };
}): WorkflowUserApprovalGraphNodeWithStateGQLType[] => {
  const { __typename: _unused, ...currentNodeBody } = currentNode;
  const foundEdge: WorkflowGraphEdgesGQLType | undefined =
    edgesByFrom[currentNodeBody.node.id];

  if (!foundEdge) {
    return [currentNodeBody];
  }

  const nextNodeId = foundEdge.to;
  const nextNode = nodesById[nextNodeId];

  return [
    currentNodeBody,
    ..._constructWorkflowGraphNodesListHelper({
      currentNode: nextNode,
      edgesByFrom,
      nodesById,
    }),
  ];
};

// this is basic crude sorting for the type of graph we have now (just user approval nodes)
const constructWorkflowGraphNodesList = (
  graph: WorkflowGraphObjectWithStateGQLType,
): WorkflowUserApprovalGraphNodeWithStateGQLType[] => {
  const nodesById = _.keyBy(graph.userApprovalNodes, (n) => n.node.id);

  const actionNodeIds = graph.actionNodes.map((a) => a.node.id);

  // sarah todo: address after jan 20 rollout
  const edgesWithoutFinalActionNode = graph.edges.filter(
    (e) => !actionNodeIds.includes(e.to),
  );

  // sarah todo: key by is only working here because the graphs are always a list for MVP, but once a node can have multiple outcoming edges
  // this will no longer apply... will need to top sort then
  const edgesByFrom = _.keyBy(edgesWithoutFinalActionNode, (e) => e.from);
  const edgesTo = new Set(edgesWithoutFinalActionNode.map((e) => e.to));

  const startingNode = graph.userApprovalNodes.filter(
    (n) => !edgesTo.has(n.node.id),
  );

  finleyAssert(
    startingNode.length === 1,
    `[Workflows] Could not find a single starting node for existing graph.`,
  );

  return _constructWorkflowGraphNodesListHelper({
    currentNode: startingNode[0],
    edgesByFrom,
    nodesById,
  });
};

const _getComparatorText = (
  comparator: WorkflowStringArrayComparatorGQLType,
) => {
  switch (comparator) {
    case 'CONTAINS_ALL_OF':
      return 'All of: ';
    case 'CONTAINS_ANY_OF':
      return 'Any of: ';
    default:
      return finleyAssertUnreachable(comparator);
  }
};

const UserApprovalConfirmationModal = ({
  header,
  body,
  confirmButtonText,
  onConfirm,
  loading,
  onClose,
}: {
  header: string;
  body: string;
  confirmButtonText: string;
  onConfirm: () => Promise<void>;
  loading: boolean;
  onClose: () => void;
}) => {
  const cancelRef = React.useRef(null);

  return (
    <AlertDialog leastDestructiveRef={cancelRef} isOpen onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {header}
          </AlertDialogHeader>

          <AlertDialogBody>{body}</AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={onClose}
              colorScheme="aurora"
              variant="outline"
              isLoading={loading}
            >
              Cancel
            </Button>
            <Button
              isLoading={loading}
              colorScheme="aurora"
              variant="solid"
              onClick={async () => {
                await onConfirm();
              }}
              ml={3}
            >
              {confirmButtonText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const UserApprovalNode = ({
  node,
  currentUserId,
  currentUserApprovalsForDomain,
  approveParams,
  rejectParams,
  doesNodeIncludeRejection,
  isActionable,
  refetchGraph,
}: {
  node: {
    users: { id: string; firstName: string; lastName: string }[];
    comparator: WorkflowStringArrayComparatorGQLType;
    visitedTimestamp?: string;
  };
  currentUserId: string;
  currentUserApprovalsForDomain: {
    [key: string]: UserApprovalForDomainGQLType;
  };
  approveParams: {
    approvalConfirmationText: string;
    onUserApprove: (params: { userId: string }) => Promise<void>;
  };
  rejectParams?: {
    rejectConfirmationText: string;
    onUserReject: (params: { userId: string }) => Promise<void>;
  };
  doesNodeIncludeRejection: boolean;
  isActionable: boolean;
  refetchGraph: () => void;
}) => {
  const [confirmationModal, setConfirmationModal] = useState<{
    action: 'approve' | 'reject';
    userId: string;
  }>();
  const [loading, setLoading] = useState<boolean>(false);

  finleyAssert(
    node.users.length > 0,
    `[Workflows] Cannot have a UserApprovalNode with no users to approve.`,
  );
  const allUsersNameText = node.users
    .map((u) => `${_.startCase(u.firstName)} ${_.startCase(u.lastName)}`)
    .join(', ');

  const comparatorText =
    node.users.length === 1 ? `` : _getComparatorText(node.comparator);

  const title = `${comparatorText}${allUsersNameText} must approve.`;

  return (
    <>
      <AccordionItem width="full">
        <AccordionButton
          _expanded={{ borderBottomWidth: '1px' }}
          bgColor={colors.background['50']}
          paddingY={0}
          paddingLeft={0}
          paddingRight="8px"
        >
          <HStack padding="16px" width="full">
            <Typography variant="detail" trim>
              {title}
            </Typography>
            <Spacer />
            {/* eslint-disable-next-line no-nested-ternary, no-nested-ternary */}
            {node.visitedTimestamp ? (
              <>
                <Typography variant="detail" trim fontWeight="semibold">
                  {`Approved ${node.visitedTimestamp}`}
                </Typography>
                <Tag
                  variant="subtle"
                  colorScheme="green"
                  borderRadius="8px"
                  padding="8px 16px"
                >
                  <Icon
                    icon="circle-check"
                    color={colors.green['300']}
                    style={{ marginRight: '8px' }}
                  />
                  <TagLabel>Approved</TagLabel>
                </Tag>
              </>
            ) : // eslint-disable-next-line no-nested-ternary
            isActionable ? (
              <></> // if actionable do not show "requires prior approval"
            ) : doesNodeIncludeRejection ? (
              <Tag
                variant="subtle"
                colorScheme="red"
                borderRadius="8px"
                padding="8px 16px"
              >
                <Icon
                  icon="circle-x"
                  color={colors.red['300']}
                  style={{ marginRight: '8px' }}
                />
                <TagLabel>Rejected</TagLabel>
              </Tag>
            ) : (
              <Tag
                variant="subtle"
                colorScheme="orange"
                borderRadius="8px"
                padding="8px 16px"
              >
                <Icon
                  icon="circle-exclamation"
                  color={colors.orange['300']}
                  style={{ marginRight: '8px' }}
                />
                <TagLabel>Requires Prior Approval</TagLabel>
              </Tag>
            )}
          </HStack>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4} padding="0">
          {node.users.map((u, index) => {
            const userApproval = currentUserApprovalsForDomain[u.id];
            const isCurrentUser = u.id === currentUserId;

            return (
              <HStack
                key={u.id}
                padding="16px"
                borderBottomWidth={
                  index === node.users.length - 1 ? '0px' : '1px'
                }
              >
                <Typography variant="detail" trim>{`${_.startCase(
                  u.firstName,
                )} ${_.startCase(u.lastName)}`}</Typography>
                <Spacer />

                {/* if user has approved or rejected, then add the timestamp here! */}
                {userApproval !== undefined && (
                  <Typography variant="detail" trim>
                    {_.startCase(userApproval.approvalStatus)} at{' '}
                    {commonDate.epochToLocaleDateString(
                      userApproval.approvalStatusAt,
                    )}
                  </Typography>
                )}
                {/* if node is not approved, then show actions if existing user otherwise show "awaiting approval" */}
                {!node.visitedTimestamp &&
                  userApproval === undefined &&
                  (isCurrentUser && isActionable ? (
                    <Flex gap="10px">
                      {rejectParams && (
                        <Button
                          variant="secondary"
                          colorScheme="v2-aurora"
                          leftIcon={<Icon icon="xmark" />}
                          isLoading={loading}
                          onClick={() => {
                            setConfirmationModal({
                              action: 'reject',
                              userId: u.id,
                            });
                          }}
                        >
                          Reject
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        colorScheme="v2-aurora"
                        leftIcon={<Icon icon="check" />}
                        isLoading={loading}
                        onClick={() => {
                          setConfirmationModal({
                            action: 'approve',
                            userId: u.id,
                          });
                        }}
                      >
                        Approve
                      </Button>
                    </Flex>
                  ) : (
                    !doesNodeIncludeRejection && (
                      <Typography variant="detail-muted" trim>
                        Awaiting Approval
                      </Typography>
                    )
                  ))}
              </HStack>
            );
          })}
        </AccordionPanel>
      </AccordionItem>

      {/* action confirmation modal */}
      {confirmationModal !== undefined && (
        <UserApprovalConfirmationModal
          header={`Confirm ${
            confirmationModal.action === 'approve' ? 'Approval' : 'Rejection'
          }`}
          body={
            confirmationModal.action === 'reject' && rejectParams
              ? rejectParams.rejectConfirmationText
              : approveParams.approvalConfirmationText
          }
          confirmButtonText={`Confirm ${
            confirmationModal.action === 'approve' ? 'Approval' : 'Rejection'
          }`}
          loading={loading}
          onClose={() => {
            setConfirmationModal(undefined);
          }}
          onConfirm={async () => {
            if (loading) {
              return;
            }

            // otherwise, try to approve/reject
            setLoading(true);
            if (confirmationModal.action === 'reject' && rejectParams) {
              await rejectParams.onUserReject({
                userId: confirmationModal.userId,
              });
            }
            if (confirmationModal.action === 'approve') {
              await approveParams.onUserApprove({
                userId: confirmationModal.userId,
              });
            }
            refetchGraph();
            setLoading(false);
            setConfirmationModal(undefined);
          }}
        />
      )}
    </>
  );
};

export const InternalApprovalWorkflowInstance = ({
  workflowInstanceId,
  currentUserApprovalsForDomain,
  approveParams,
  rejectParams,
  readOnly = false,
}: {
  workflowInstanceId: string;
  currentUserApprovalsForDomain: {
    [key: string]: UserApprovalForDomainGQLType;
  };
  approveParams: {
    approvalConfirmationText: string;
    onUserApprove: (params: { userId: string }) => Promise<void>;
  };
  rejectParams?: {
    rejectConfirmationText: string;
    onUserReject: (params: { userId: string }) => Promise<void>;
    allowMultipleRejections?: boolean; // this is usually false
  };
  readOnly?: boolean;
}) => {
  const {
    user: { id: userId, organizationId },
  } = useUserAuthOrThrow();

  const workflowInstanceWithStateQuery = useGetWorkflowInstanceWithStateQuery({
    fetchPolicy: 'network-only',
    variables: {
      workflowInstanceId,
    },
  });
  if (workflowInstanceWithStateQuery.error) {
    Sentry.captureException(workflowInstanceWithStateQuery.error);
    throw new Error(
      `Error fetching workflow instance with state for id ${workflowInstanceId}`,
    );
  }
  const sortedUserApprovalNodes = useMemo(() => {
    if (workflowInstanceWithStateQuery.data) {
      const workflowInstanceWithState =
        workflowInstanceWithStateQuery.data.getWorkflowInstanceWithState;

      // sarah todo: address after jan 20th (how much can be generalized across non internal approval stuff)
      finleyAssert(
        workflowInstanceWithState.userApprovalNodes.length > 0,
        `[Workflows] Expects user approval nodes to always be configured.`,
      );
      finleyAssert(
        workflowInstanceWithState.actionNodes.length === 1,
        `[Workflows] Expects only one action node that is also the final action.`,
      );

      return constructWorkflowGraphNodesList(workflowInstanceWithState);
    }
    return [];
  }, [workflowInstanceWithStateQuery.data]);

  const firstNonVisitedNode: {
    id: string;
    index: number;
  } | null = useMemo(() => {
    const index = sortedUserApprovalNodes.findIndex(
      (n) => !n.state.visitedTimestamp,
    );

    return index < 0
      ? null
      : { id: sortedUserApprovalNodes[index].node.id, index };
  }, [sortedUserApprovalNodes]);

  const rejectionUserIds = useMemo(() => {
    return Object.values(currentUserApprovalsForDomain)
      .filter((approval) => approval.approvalStatus === 'rejected')
      .map((approval) => approval.user.id);
  }, [currentUserApprovalsForDomain]);

  const graphHasBeenRejected = useMemo(() => {
    return (
      rejectParams &&
      rejectParams.allowMultipleRejections !== true &&
      rejectionUserIds.length > 0
    );
  }, [rejectParams, rejectionUserIds.length]);

  const organizationQuery = useGetOrganizationQuery({
    variables: {
      organizationId,
    },
  });
  if (organizationQuery.error) {
    Sentry.captureException(organizationQuery.error);
    throw new Error(`Error fetching organization for id ${organizationId}`);
  }
  const allUsersForOrganizationById = useMemo(() => {
    if (organizationQuery.data) {
      const {
        users: allUsersForOrganization,
      } = organizationQuery.data.organization;

      return _.keyBy(allUsersForOrganization, (u) => u.id);
    }

    return {};
  }, [organizationQuery.data]);

  if (
    !workflowInstanceWithStateQuery.data ||
    workflowInstanceWithStateQuery.loading ||
    !organizationQuery.data
  ) {
    return <Skeleton h="400px" />;
  }

  return (
    <Accordion
      width="full"
      defaultIndex={firstNonVisitedNode ? [firstNonVisitedNode.index] : []}
      allowMultiple
      borderLeftWidth="1px"
      borderRightWidth="1px"
    >
      {sortedUserApprovalNodes.map((n) => {
        const node = {
          users: n.node.userIds.map((id) => {
            const { firstName, lastName } = allUsersForOrganizationById[id];
            return { id, firstName, lastName };
          }),
          comparator: n.node.comparator,
          visitedTimestamp: n.state.visitedTimestamp
            ? commonDate.epochToLocaleDateString(n.state.visitedTimestamp)
            : undefined,
        };

        const isFirstNonVisitedNode = n.node.id === firstNonVisitedNode?.id;

        // if graph is neither "read only" nor rejected and this is the first non visited node, then the node is actionable
        const isActionable =
          !readOnly && !graphHasBeenRejected && isFirstNonVisitedNode;

        // is actionable takes precedence over if a node includes a rejection
        // (e.g. if the node is still actionable even if one of its users have rejected it, we will show "actionable view")
        const doesNodeIncludeRejection = node.users.some((u) =>
          rejectionUserIds.includes(u.id),
        );

        return (
          <UserApprovalNode
            key={n.node.id}
            node={node}
            currentUserId={userId}
            currentUserApprovalsForDomain={currentUserApprovalsForDomain}
            approveParams={approveParams}
            rejectParams={rejectParams}
            refetchGraph={workflowInstanceWithStateQuery.refetch}
            isActionable={isActionable}
            doesNodeIncludeRejection={doesNodeIncludeRejection}
          />
        );
      })}
    </Accordion>
  );
};
